import React, { useEffect, useRef, useState } from "react";
import { FiMic, FiSquare } from "react-icons/fi";

const getSpeechRecognition = () => {
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

export const DictationButton = ({ text, setText, onSend, onListeningChange }) => {
  const [isListening, setIsListening] = useState(false);

  const recognitionRef = useRef(null);
  const isThisInstanceListening = useRef(false);
  const originalText = useRef(text);

  const setListeningState = (value) => {
    setIsListening(value);
    onListeningChange?.(value);
  };

  const vibrate = (duration = 15) => {
    if (navigator.vibrate) {
      navigator.vibrate(duration);
    }
  };

  const stopListening = () => {
    isThisInstanceListening.current = false;
    setListeningState(false);

    try {
      recognitionRef.current?.stop();
    } catch {
      // Ignore invalid state errors
    }
  };

  const startListening = async () => {
    const SpeechRecognition = getSpeechRecognition();

    if (!SpeechRecognition) {
      window.alert("Speech recognition is not supported in this browser.");
      return;
    }

    try {
      if (navigator.mediaDevices?.getUserMedia) {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });

        stream.getTracks().forEach((track) => track.stop());
      }
    } catch {
      window.alert("Please grant microphone access to use dictation.");
      return;
    }

    const recognition = new SpeechRecognition();

    recognition.lang = "en-US";
    recognition.interimResults = true;
    recognition.continuous = false;

    recognition.onstart = () => {
      if (!isThisInstanceListening.current) return;

      setListeningState(true);
      vibrate(25);
    };

    recognition.onend = () => {
      if (!isThisInstanceListening.current) return;

      setListeningState(false);
      isThisInstanceListening.current = false;

      if (onSend) {
        setTimeout(() => onSend(), 100);
      }
    };

    recognition.onerror = (event) => {
      if (!isThisInstanceListening.current) return;

      console.log("Speech error:", event.error, event.message);

      setListeningState(false);
      isThisInstanceListening.current = false;
    };

    recognition.onresult = (event) => {
      if (!isThisInstanceListening.current) return;

      const transcript = Array.from(event.results)
        .map((result) => result[0]?.transcript || "")
        .join("");

      const needsSpace =
        originalText.current.length > 0 &&
        !originalText.current.endsWith(" ");

      setText(`${originalText.current}${needsSpace ? " " : ""}${transcript}`);
    };

    recognitionRef.current = recognition;
    isThisInstanceListening.current = true;
    originalText.current = text;

    try {
      recognition.start();
    } catch (error) {
      console.log("Speech start error:", error);
      setListeningState(false);
      isThisInstanceListening.current = false;
    }
  };

  const toggleListening = () => {
    vibrate(10);

    if (isListening) {
      stopListening();
      return;
    }

    startListening();
  };

  useEffect(() => {
    return () => {
      isThisInstanceListening.current = false;

      try {
        recognitionRef.current?.abort();
      } catch {
        // Ignore cleanup errors
      }
    };
  }, []);

  return (
    <div className="ml-1 flex h-9 w-9 items-center justify-center">
      <button
        type="button"
        onClick={toggleListening}
        className="flex h-full w-full items-center justify-center rounded-full transition-opacity hover:opacity-80 active:opacity-70"
        aria-label={isListening ? "Stop dictation" : "Start dictation"}
      >
        {isListening ? (
          <FiSquare size={20} color="#0F172A" />
        ) : (
          <FiMic size={20} color="#818791" />
        )}
      </button>
    </div>
  );
};
